import * as i0 from '@angular/core';
import { Component, Optional, Self, HostBinding, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { coerceBooleanProperty, getUId } from '@roosevelt/common-ui-lib/core';
import * as i1 from '@angular/forms';

/**
 * Radio Component
 *
 * @description typical radio input you would find in a form
 * @example
 * <!-- rs-doc {link: 'Example of basic radio'} -->
 * <rs-radio id="1" name="a" label="option 1" value="a"></rs-radio>
 * <rs-radio id="2" name="a" label="option 2" value="b"></rs-radio>
 * @example
 * <!-- rs-doc {link: 'Example of radios with disabled and secondary'} -->
 * <rs-radio color="secondary" id="5" name="c" label="enabled 1" value="a"></rs-radio>
 * <rs-radio color="secondary" disabled id="6" name="c" label="disabled 1" checked value="b"></rs-radio>
 * <rs-radio color="secondary" id="7" name="c" label="enabled 2" value="b"></rs-radio>
 * <rs-radio color="secondary" [errors]="{required: true}" id="8" name="c" label="value" value="c"></rs-radio>
 * @example
 * <!-- rs-doc {link: 'Example of grouped horizontally'} -->
 * <p>Please select an option</p>
 * <div><rs-radio id="3" name="b" label="item 1" value="a"></rs-radio></div>
 * <div><rs-radio id="4" name="b" label="item 2" value="b"></rs-radio></div>
 * @example
 * <!-- rs-doc {link: 'Example of radio groups'} -->
 * <p>
 * <span style="width:67px; display: inline-block">In</span>
 * <span>Out</span>
 * </p>
 * <p>
 * <rs-radio id="text1" name="text" value="a"></rs-radio>
 * <rs-radio id="text2" name="text" label="text" value="b"></rs-radio>
 * </p>
 * <p>
 * <rs-radio id="phone1" name="phone" value="a"></rs-radio>
 * <rs-radio id="phone2" name="phone" label="phone" value="b"></rs-radio>
 * </p>
 * <p>
 * <rs-radio id="email1" name="email" value="a"></rs-radio>
 * <rs-radio id="email2" name="email" label="email" value="b"></rs-radio>
 * </p>
 */
class RadioComponent {
    /** template driven way to set the value */
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
    }
    /** applies tabindex to underlying input */
    get tabindex() {
        return this._tabindex;
    }
    set tabindex(value) {
        this._tabindex = value;
        this.tabBinding = '__reset__';
    }
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    /** template driven way to show a check status */
    get checked() {
        return this._checked;
    }
    set checked(checked) {
        this._checked = coerceBooleanProperty(checked);
        if (this._checked) {
            this.onChange(this.value);
        }
    }
    /** id applied to underlying control and label */
    get id() {
        return this._id;
    }
    set id(value) {
        this._id = value;
        this.idBinding = '__reset__';
    }
    /** name applied to underlying control to group radios */
    get name() {
        return this._name;
    }
    set name(value) {
        this._name = value;
        this.nameBinding = '__reset__';
    }
    /** template driven way to apply disabled */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this.setDisabledState(coerceBooleanProperty(value));
    }
    constructor(elementRef, _parentForm, _parentFormGroup, ngControl) {
        this.elementRef = elementRef;
        this._parentForm = _parentForm;
        this._parentFormGroup = _parentFormGroup;
        this.ngControl = ngControl;
        this.idBinding = undefined;
        this.nameBinding = undefined;
        this.tabBinding = undefined;
        /** This is the text to the right of the check mark */
        this.label = '';
        /** Should not show errors */
        this.hideError = false;
        /** whether we should show errors right away or after form is submitted */
        this.validation = 'lazy';
        /** template driven errors following the reactive form style. for example this would show required {required: true}. follow the error message map provider */
        this.errors = null;
        this.parent = undefined;
        this._disabled = false;
        this._value = undefined;
        this._color = 'primary';
        this._name = undefined;
        this._id = getUId();
        this._checked = false;
        this._tabindex = '';
        this.onChange = () => { };
        this.onTouched = () => { };
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }
    /* Implemented as part of ControlValueAccessor. */
    writeValue(value) {
        if (value || value === false) {
            this.checked = value === this.value;
        }
    }
    /* Implemented as part of ControlValueAccessor. */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /* Implemented as part of ControlValueAccessor. */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    change(event) {
        this.checked = event.target.checked;
    }
    touch() {
        this.onTouched();
    }
    ngOnInit() {
        this.color = this.color;
        this.parent = this._parentFormGroup || this._parentForm;
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_disabled = undefined; }
    static { this.ngAcceptInputType_checked = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RadioComponent, deps: [{ token: i0.ElementRef }, { token: i1.NgForm, optional: true }, { token: i1.FormGroupDirective, optional: true }, { token: i1.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: RadioComponent, isStandalone: true, selector: "rs-radio", inputs: { label: "label", hideError: "hideError", validation: "validation", errors: "errors", value: "value", tabindex: "tabindex", color: "color", checked: "checked", id: "id", name: "name", disabled: "disabled" }, host: { properties: { "attr.id": "this.idBinding", "attr.name": "this.nameBinding", "attr.tabindex": "this.tabBinding" } }, ngImport: i0, template: "<label [for]=\"id\" class=\"container-radio\" [class.disabled]=\"disabled\">\n    <input [class]=\"{'alert-for-inputs':ngControl && ngControl.invalid && (ngControl.touched || parent.submitted)}\" [name]=\"name\"\n    [id]=\"id\" type=\"radio\" [checked]=\"checked\" aria-label=\"radio\" [value]=\"value\" (change)=\"change($event)\" (blur)=\"touch()\" [disabled]=\"disabled\" [tabindex]=\"tabindex\">\n    <span class=\"radio-dot\" [class]=\"{\n      'invalid': ((ngControl && ngControl.invalid) || (!ngControl && errors)) && ((validation === 'lazy' && (!ngControl || parent.submitted || ngControl.touched)) || validation === 'eager'),\n      'valid': ((ngControl && !ngControl.invalid) || (!ngControl && !errors)) && validation === 'eager'\n    }\">\n    </span>\n    <span class=\"radio-dot-focus\"></span>\n    <span>{{label}}</span>\n</label>\n\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}.container-radio{margin-bottom:12px;position:relative;display:inline-flex;align-items:center;margin-right:30px;cursor:pointer;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.container-radio.disabled{cursor:not-allowed}.container-radio input{position:absolute;opacity:0;cursor:pointer}.container-radio input:disabled{cursor:not-allowed}.container-radio input:focus~.radio-dot-focus{position:absolute;height:31px;width:31px;left:1px;display:inline-block;border-width:2px;border-style:solid;border-radius:50%}.radio-dot{position:relative;height:25px;width:25px;border-radius:50%;margin:2px 8px 2px 4px;border:2px solid}.radio-dot.invalid{border-color:#a94442!important}.radio-dot.valid{border-color:#42a948!important}.radio-dot:after{position:absolute;height:15px;width:15px;border-radius:50%;top:3px;left:3px;content:\"\"}.fieldset-inline .container-radio{margin-right:20px}.fieldset-block .container-radio{margin-bottom:15px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-radio', standalone: true, template: "<label [for]=\"id\" class=\"container-radio\" [class.disabled]=\"disabled\">\n    <input [class]=\"{'alert-for-inputs':ngControl && ngControl.invalid && (ngControl.touched || parent.submitted)}\" [name]=\"name\"\n    [id]=\"id\" type=\"radio\" [checked]=\"checked\" aria-label=\"radio\" [value]=\"value\" (change)=\"change($event)\" (blur)=\"touch()\" [disabled]=\"disabled\" [tabindex]=\"tabindex\">\n    <span class=\"radio-dot\" [class]=\"{\n      'invalid': ((ngControl && ngControl.invalid) || (!ngControl && errors)) && ((validation === 'lazy' && (!ngControl || parent.submitted || ngControl.touched)) || validation === 'eager'),\n      'valid': ((ngControl && !ngControl.invalid) || (!ngControl && !errors)) && validation === 'eager'\n    }\">\n    </span>\n    <span class=\"radio-dot-focus\"></span>\n    <span>{{label}}</span>\n</label>\n\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}.container-radio{margin-bottom:12px;position:relative;display:inline-flex;align-items:center;margin-right:30px;cursor:pointer;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.container-radio.disabled{cursor:not-allowed}.container-radio input{position:absolute;opacity:0;cursor:pointer}.container-radio input:disabled{cursor:not-allowed}.container-radio input:focus~.radio-dot-focus{position:absolute;height:31px;width:31px;left:1px;display:inline-block;border-width:2px;border-style:solid;border-radius:50%}.radio-dot{position:relative;height:25px;width:25px;border-radius:50%;margin:2px 8px 2px 4px;border:2px solid}.radio-dot.invalid{border-color:#a94442!important}.radio-dot.valid{border-color:#42a948!important}.radio-dot:after{position:absolute;height:15px;width:15px;border-radius:50%;top:3px;left:3px;content:\"\"}.fieldset-inline .container-radio{margin-right:20px}.fieldset-block .container-radio{margin-bottom:15px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i1.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }], propDecorators: { idBinding: [{
                type: HostBinding,
                args: ['attr.id']
            }], nameBinding: [{
                type: HostBinding,
                args: ['attr.name']
            }], tabBinding: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], label: [{
                type: Input
            }], hideError: [{
                type: Input
            }], validation: [{
                type: Input
            }], errors: [{
                type: Input
            }], value: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], color: [{
                type: Input
            }], checked: [{
                type: Input
            }], id: [{
                type: Input
            }], name: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

class RadioModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: RadioModule, imports: [CommonModule,
            RadioComponent], exports: [RadioComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RadioModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RadioComponent
                    ],
                    exports: [RadioComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RadioComponent, RadioModule };
